import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Link } from "react-router-dom";

import { PostDataAPI } from "../components/services/ApiServices";
import { formatPhoneNumber } from "./../components/services/extension";

// ─── Initialize Stripe once, outside any component ───────────────────────────
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
);

// ─── Stripe appearance: dark/gold theme to match the site ────────────────────
const stripeAppearance = {
  theme: "night",
  variables: {
    colorBackground: "transparent",
    colorText: "#f0f0f0",
    colorDanger: "#c70000",
    fontFamily: "sans-serif",
    borderRadius: "0px",
  },
  rules: {
    ".Input": {
      border: "1px solid #3E3620",
      backgroundColor: "transparent",
      color: "#f0f0f0",
      padding: "13px 11px",
    },
    ".Input:focus": {
      border: "1px solid #b8922a",
      outline: "none",
      boxShadow: "none",
    },
    ".Label": {
      color: "#c0c0be",
      marginBottom: "6px",
    },
    ".Tab": {
      border: "1px solid #3E3620",
      backgroundColor: "transparent",
      color: "#c0c0be",
    },
    ".Tab--selected": {
      border: "1px solid #b8922a",
      color: "#f0f0f0",
    },
    ".Tab:hover": {
      color: "#f0f0f0",
    },
    ".Error": {
      color: "#c70000",
    },
  },
};

const stripeCardElementOptions = {
  style: {
    base: {
      color: "#f0f0f0",
      fontFamily: "sans-serif",
      fontSize: "16px",
      fontSmoothing: "antialiased",
      "::placeholder": {
        color: "#9a9a9a",
      },
      iconColor: "#caa43a",
    },
    invalid: {
      color: "#ff4d4f",
      iconColor: "#ff4d4f",
    },
  },
  hidePostalCode: true,
};

// ─── Inner component — must live inside <Elements> to use Stripe hooks ────────
function StripePaymentForm({ total, quantity, navigate, orderId, clientSecret, customerData }) {
  const stripe = useStripe();
  const elements = useElements();

  const [paymentError, setPaymentError] = useState(null);
  const [paymentMessage, setPaymentMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Poll backend status API until "Approved"/"Paid" or timeout
  const pollBackendStatus = async (orderIdToPoll, maxAttempts = 6, delayMs = 2000) => {
    const apiBase = sessionStorage.getItem("server_api_url") || "";
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const res = await fetch(`${apiBase}payments/stripe/status/${orderIdToPoll}`);
        const json = await res.json();
        const status = (json?.data?.paymentStatus || "").toLowerCase();
        if (status === "approved" || status === "paid") {
          return "paid";
        }
      } catch (_) {
        // ignore transient errors, keep polling
      }
      if (attempt < maxAttempts - 1) {
        await new Promise((r) => setTimeout(r, delayMs));
      }
    }
    return "pending"; // webhook hasn't fired yet within poll window
  };

  const [totalPaymentData, setTotalPaymentData] = useState([{}]);

  const loadShipmentDetail = () => {
    const obj = {
      orderId: orderId
    }
    PostDataAPI('payments/stripe/getPaymentDetail', obj, true).then((result) => {
      if (result.success == true) {
        setTotalPaymentData(result.data)
      }
    });
  }


  useEffect(() => {
    loadShipmentDetail();
  }, [])

  const handlePayNow = async () => {
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setPaymentError(null);
    setPaymentMessage(null);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement || !clientSecret) {
      setPaymentError("Payment form is not ready. Please try again.");
      setIsProcessing(false);
      return;
    }

    // ── Step 1: Confirm card payment with Stripe ───────────────────────────
    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: `${customerData?.FirstName || ""} ${customerData?.LastName || ""}`.trim(),
          email: customerData?.Email || "",
          phone: customerData?.Phone || "",
          address: {
            country: "US",
            postal_code: customerData?.Zip || "",
          },
        },
      },
    });

    // ── Step 2: Handle confirmCardPayment result ───────────────────────────
    if (result.error) {
      // Card declined, invalid details, etc. — show Stripe's own message
      setPaymentError(result.error.message);
      setIsProcessing(false);
      return;
    }

    const paymentIntent = result.paymentIntent;

    if (!paymentIntent) {
      // No paymentIntent returned — do not navigate, treat as incomplete
      setPaymentError(
        "Payment could not be confirmed. Please try again."
      );
      setIsProcessing(false);
      return;
    }

    const stripeStatus = paymentIntent.status;

    if (stripeStatus === "requires_payment_method") {
      setPaymentError(
        "Payment was not completed. Please check your card details and try again."
      );
      setIsProcessing(false);
      return;
    }

    if (stripeStatus === "requires_action") {
      setPaymentError(
        "Additional authentication is required. Please complete the verification step."
      );
      setIsProcessing(false);
      return;
    }

    if (stripeStatus !== "succeeded" && stripeStatus !== "processing") {
      setPaymentError(
        "Payment was not completed. Please try again."
      );
      setIsProcessing(false);
      return;
    }

    // ── Step 3: Stripe says succeeded or processing — verify with backend ─
    // Webhook is the source of truth for DB; we poll the status API briefly.
    setPaymentMessage("Payment received. Confirming your order...");

    const backendStatus = await pollBackendStatus(orderId);

    setIsProcessing(false);

    if (backendStatus === "paid") {
      // Backend confirmed — navigate to thank you page
      navigate("/thankyou");
    } else {
      // Webhook hasn't updated DB yet (or Stripe status is "processing")
      // Show a safe in-between message — do NOT show final order success
      setPaymentMessage(null);
      setPaymentError(null);
      navigate("/thankyou"); // navigate anyway — webhook will update DB shortly
      // Note: the thankyou page shows "order placed" which is appropriate
      // because Stripe has already confirmed the charge. The webhook will
      // update the DB within seconds. We do NOT mark DB paid here.
    }
  };

  return (
    <>
      {/* ── Payment Details card — same outer structure as before ── */}
      <div className="card mb-4 p-2">
        <div className="card-header py-3" style={{ borderBottom: '1px solid #3d331c' }}>
          <h4 className="mb-0">Total</h4>
        </div>

        <div className="card-body">
          <div className="col-12" style={{ margin: '10px 0px' }}>
            <div style={{ fontSize: '1rem' }}>
              <b>Book Price:</b> <b style={{ float: 'right' }}>${Number(250 * quantity).toFixed(2)}</b>
            </div>
          </div>
          <div className="col-12" style={{ margin: '10px 0px' }}>
            <div style={{ fontSize: '1rem' }}>
              <b>Quantity:</b> <b style={{ float: 'right' }}>{quantity}</b>
            </div>
          </div>
          <div className="col-12" style={{ margin: '10px 0px' }}>
            <div style={{ fontSize: '1rem' }}>
              <b>Tax:</b> <b style={{ float: 'right' }}>${Number(totalPaymentData.taxCharges).toFixed(2)}</b>
            </div>
          </div>

          <div className="col-12" style={{ margin: '10px 0px' }}>
            <div style={{ fontSize: '1rem' }}>
              <b>Shipment Cost:</b> <b style={{ float: 'right' }}>Free Shipping</b>
            </div>
          </div>

          <div className="col-12" style={{ margin: '10px 0px' }}>
            <div style={{ fontSize: '1rem' }}>
              <b>Total Cost:</b> <b style={{ float: 'right' }}>${Number(totalPaymentData.orderTotalAmount).toFixed(2)}</b>
            </div>
          </div>

        </div>

      </div>
      <div className="card mb-4 p-2">
        <div className="card-header py-3">
          <h4 className="mb-0">Payment Details</h4>
        </div>

        <div className="card-body">
          {/* Stripe Card Element — card only */}
          <div style={{ paddingBottom: "12px" }}>
            <div className="stripe-card-label">Card Information</div>
            <div className="stripe-card-wrapper">
              <CardElement
                id="stripe-card-element"
                options={stripeCardElementOptions}
              />
            </div>
          </div>

          {/* Amount line — same as before */}
          <div className="col-12" style={{ marginTop: 10 }}>
            <div style={{ fontSize: 14 }}>
              Amount to charge: <b>${Number(totalPaymentData.orderTotalAmount).toFixed(2)}</b>
            </div>
          </div>

          {/* Processing / status message */}
          {paymentMessage && !paymentError && (
            <div style={{ marginTop: 10, color: "#f0f0f0", fontSize: 14 }}>
              <span className="button-spinner me-2"></span>
              {paymentMessage}
            </div>
          )}

          {/* Inline Stripe error (e.g. card declined, incomplete payment) */}
          {paymentError && (
            <div
              className="field_errors"
              style={{ marginTop: 8, color: "#c70000", fontSize: 14 }}
            >
              {paymentError}
            </div>
          )}
        </div>
      </div>

      {/* ── Pay Now button — same className/text as before ── */}
      <button
        className="w-100 btn btn-primary submit_btn btn_black"
        type="button"
        id="pay-now-btn"
        onClick={handlePayNow}
        disabled={!stripe || !elements || isProcessing}
      >
        {isProcessing ? <span className="button-spinner me-2"></span> : null}{" "}
        Pay Now
      </button>
    </>
  );
}

// ─── Main CheckoutForm component ──────────────────────────────────────────────
function CheckoutForm({ setTotalPrice, ...props }) {
  const navigate = useNavigate();

  const [errorMessages, setErrorMessages] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [paymentEnable, setPaymentEnable] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [errorState, setErrorState] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentError, setPaymentIntentError] = useState(null);

  const [customerData, setCustomerData] = useState({
    FirstName: "",
    LastName: "",
    Email: "",
    Phone: "",
    Address: "",
    Appartment: "",
    country: "",
    State: "",
    Zip: "",
    City: "",
  });

  const [price, setPrice] = useState(0);
  const [totalQuantity, setTotalQuantity] = useState(0);

  const loadQuantity = () => {
    const obj = {
      ProductId: 55
    }
    PostDataAPI('product/getCurrentQuantity', obj, true).then((result) => {
      if (result.success == true) {
        setTotalQuantity(result.data.quantity)
        setPrice(result.data.price)
      }
    });
  }


  useEffect(() => {
    loadQuantity();
  }, [])


  const unitPrice = price;


  const [agreed, setAgreed] = useState(true);


  const [quantity, setQuantity] = useState(1);


  const [productData, setProductData] = useState([
    {
      ProductName: "America 250",
      Quantity: String(quantity),
      Price: String(unitPrice),
      Details:
        "Limited edition release of 1776 copies, each personally inscribed with a message from ron desantis",
      type: "product",
    },
  ]);

  useEffect(() => {
    setProductData((prev) =>
      prev.map((p, idx) =>
        idx === 0
          ? { ...p, Quantity: String(quantity), Price: String(unitPrice) }
          : p
      )
    );
  }, [quantity, unitPrice]);

  // ── Fetch PaymentIntent clientSecret once the order is confirmed ──────────
  useEffect(() => {
    if (!paymentEnable || !orderData) return;

    const { orderId, customerId } = orderData;
    if (!orderId || !customerId) return;

    PostDataAPI(
      "payments/stripe/create-payment-intent",
      { orderId, customerId },
      true
    )
      .then((result) => {
        if (result?.success === true && result?.data?.clientSecret) {
          setClientSecret(result.data.clientSecret);
        } else {
          setPaymentIntentError(
            "Unable to initialize payment. Please try again."
          );
        }
      })
      .catch(() => {
        setPaymentIntentError(
          "Unable to initialize payment. Please try again."
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentEnable, orderData]);

  const total = useMemo(() => {
    return Number((unitPrice * quantity).toFixed(2));
  }, [unitPrice, quantity]);



  const handleChange = (e) => {
    const { name, value } = e.target;
    setCustomerData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    const { name } = e.target;
    let raw = (e.target.value || "").replace(/\D/g, "").slice(0, 10);

    const match = raw.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    const formatted = match
      ? [
        match[1] ? "(" + match[1] : "",
        match[2] ? ") " + match[2] : "",
        match[3] ? "-" + match[3] : "",
      ].join("")
      : e.target.value;

    setCustomerData((prev) => ({ ...prev, [name]: formatted }));
  };

  const handleChangeQuantity = (e) => {
    // Remove all non-numeric characters
    let value = e.target.value.replace(/\D/g, "");

    // Limit to 6 digits
    value = value.slice(0, 6);

    setQuantity(value);
    //setTotalPrice(250 * value)
  };



  const Validate = (errorList) => {
    const nextErrors = {};

    if (!customerData.FirstName?.trim()) nextErrors.errorFirstName = true;
    if (!customerData.LastName?.trim()) nextErrors.errorLastName = true;

    if (!agreed) {
      nextErrors.errorAgree = true;
    }

    if (!quantity || Number(quantity) < 1) {
      nextErrors.errorQuantity = "Quantity is required";
    } else if (Number(quantity) === 0) {
      nextErrors.errorQuantity = "Quantity cannot be 0";
    } else if (Number(quantity) > totalQuantity) {
      nextErrors.errorQuantity = `We currently have only ${totalQuantity} books in stock.`;
    }


    const email = customerData.Email?.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      nextErrors.errorEmail = true;

    if (!customerData.Phone?.trim()) nextErrors.errorPhone = true;
    if (!customerData.City?.trim()) nextErrors.errorCity = true;
    if (!customerData.Address?.trim()) nextErrors.errorAddress = true;
    if (!customerData.State?.trim()) nextErrors.errorState = true;
    if (!customerData.Zip?.trim()) nextErrors.errorZip = true;

    setErrorMessages((prev) => ({ ...prev, ...nextErrors }));
    Object.keys(nextErrors).forEach(() => errorList.push(true));
  };


  const [showingError, setShowingError] = useState('');
  const Checkout = () => {
    const errorList = [];
    Validate(errorList);
    setErrorState(null);

    if (errorList.length < 1) {
      const formattedProducts = Array.isArray(productData)
        ? productData
        : [productData];
      const shippingQty = formattedProducts.reduce(
        (totalQty, product) => totalQty + Number(product.Quantity || product.quantity || quantity || 0),
        0
      );

      const obj = {
        OrderType: "ManualOrder",
        Customer: customerData,
        ShippingDetail: {
          ToZipCode: customerData.Zip,
          Qty: shippingQty,
        },
        products: formattedProducts,
        TotalAmount: total,
      };

      setIsLoading(true);
      PostDataAPI("figgorder/SaveCustomerOrder", obj, true)
        .then((result) => {
          setIsLoading(false);
          if (result?.success == false) {
            setErrorState(result?.message);
            setShowingError(result?.message)
            return;
          }
          const { customerId, orderId } = result.data || {};
          if (!orderId || orderId === 0) {
            setErrorState(
              "Order was not created. Please check your details and try again."
            );
            return;
          }
          if (!customerId || customerId === 0) {
            setErrorState("Unable to create order. Please try again.");
            return;
          }
          setOrderData(result.data);
          setPaymentEnable(true);
        })
        .catch(() => {
          setIsLoading(false);
          setErrorState("Unable to create order. Please try again.");
        });
    }
  };



  return (
    <>
      {!paymentEnable ? (
        // ── Step 1: Quantity + Shipping ──────────────────────────────────────
        <>
          <div className="card mb-4 p-2">
            <div className="card-header">
              <h4 className="mb-0">Quantity</h4>
            </div>

            <div className="card-body pt-2">
              <input
                type="text"
                id="quantity"
                name="Quantity"
                className="form-control"
                value={quantity}
                onChange={handleChangeQuantity}
                maxLength={5}
                placeholder="Enter quantity"
                required
              />
              {errorMessages.errorQuantity && (
                <span className="field_errors" style={{ color: "#c70000" }}>
                  {errorMessages.errorQuantity}
                </span>
              )}


              {/* <select
                onChange={handleChangeQuantity}
                value={quantity}
                className="form-select"
                name="Quantity"
                id="quantity"
                required
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select> */}
            </div>
          </div>

          <div className="card mb-4 p-2">
            <div className="card-header py-3">
              <h4 className="mb-0">Shipping address</h4>
            </div>

            <div className="card-body">
              <div className="row g-3">
                <div className="col-sm-6 my-1">
                  <input
                    type="text"
                    className="form-control"
                    id="FirstName"
                    name="FirstName"
                    value={customerData.FirstName}
                    onChange={handleChange}
                    placeholder="First name"
                    required
                  />
                  {errorMessages.errorFirstName ? (
                    <span className="field_errors" style={{ color: "#c70000" }}>
                      First name is required.
                    </span>
                  ) : null}
                </div>

                <div className="col-sm-6 my-1">
                  <input
                    type="text"
                    className="form-control"
                    id="LastName"
                    name="LastName"
                    value={customerData.LastName}
                    onChange={handleChange}
                    placeholder="Last name"
                    required
                  />
                  {errorMessages.errorLastName ? (
                    <span className="field_errors" style={{ color: "#c70000" }}>
                      Last name is required.
                    </span>
                  ) : null}
                </div>

                <div className="col-sm-6 my-1">
                  <input
                    type="email"
                    className="form-control"
                    id="Email"
                    name="Email"
                    value={customerData.Email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    required
                  />
                  {errorMessages.errorEmail ? (
                    <span className="field_errors" style={{ color: "#c70000" }}>
                      Please enter a valid email address
                    </span>
                  ) : null}
                </div>

                <div className="col-sm-6 my-1">
                  <input
                    className="form-control"
                    id="Phone"
                    name="Phone"
                    value={formatPhoneNumber(customerData.Phone)}
                    onChange={handlePhoneChange}
                    required
                    type="text"
                    placeholder="Phone"
                    maxLength={14}
                  />
                  {errorMessages.errorPhone ? (
                    <span className="field_errors" style={{ color: "#c70000" }}>
                      Please enter a valid phone number
                    </span>
                  ) : null}
                </div>

                <div className="col-12 my-1">
                  <input
                    type="text"
                    className="form-control"
                    id="Address"
                    name="Address"
                    onChange={handleChange}
                    value={customerData.Address}
                    placeholder="1234 Main St (Deliveries not available on PO Box Number)"
                    required
                  />
                  {errorMessages.errorAddress ? (
                    <span className="field_errors" style={{ color: "#c70000" }}>
                      Please enter address
                    </span>
                  ) : null}
                </div>

                <div className="col-12 my-1">
                  <input
                    type="text"
                    className="form-control"
                    id="Appartment"
                    name="Appartment"
                    value={customerData.Appartment}
                    onChange={handleChange}
                    placeholder="Apartment or suite"
                  />
                </div>

                <div className="col-md-4 my-1">
                  <input
                    type="text"
                    className="form-control"
                    id="City"
                    name="City"
                    value={customerData.City}
                    onChange={handleChange}
                    placeholder="City"
                  />
                  {errorMessages.errorCity ? (
                    <span className="field_errors" style={{ color: "#c70000" }}>
                      Please enter city
                    </span>
                  ) : null}
                </div>

                <div className="col-md-4 my-1">
                  <select
                    onChange={handleChange}
                    value={customerData.State}
                    className="form-select"
                    name="State"
                    id="State"
                    required
                  >
                    <option value="">State...</option>
                    <option value="AL">Alabama</option>
                    <option value="AK">Alaska</option>
                    <option value="AZ">Arizona</option>
                    <option value="AR">Arkansas</option>
                    <option value="CA">California</option>
                    <option value="CO">Colorado</option>
                    <option value="CT">Connecticut</option>
                    <option value="DE">Delaware</option>
                    <option value="FL">Florida</option>
                    <option value="GA">Georgia</option>
                    <option value="HI">Hawaii</option>
                    <option value="ID">Idaho</option>
                    <option value="IL">Illinois</option>
                    <option value="IN">Indiana</option>
                    <option value="IA">Iowa</option>
                    <option value="KS">Kansas</option>
                    <option value="KY">Kentucky</option>
                    <option value="LA">Louisiana</option>
                    <option value="ME">Maine</option>
                    <option value="MD">Maryland</option>
                    <option value="MA">Massachusetts</option>
                    <option value="MI">Michigan</option>
                    <option value="MN">Minnesota</option>
                    <option value="MS">Mississippi</option>
                    <option value="MO">Missouri</option>
                    <option value="MT">Montana</option>
                    <option value="NE">Nebraska</option>
                    <option value="NV">Nevada</option>
                    <option value="NH">New Hampshire</option>
                    <option value="NJ">New Jersey</option>
                    <option value="NM">New Mexico</option>
                    <option value="NY">New York</option>
                    <option value="NC">North Carolina</option>
                    <option value="ND">North Dakota</option>
                    <option value="OH">Ohio</option>
                    <option value="OK">Oklahoma</option>
                    <option value="OR">Oregon</option>
                    <option value="PA">Pennsylvania</option>
                    <option value="RI">Rhode Island</option>
                    <option value="SC">South Carolina</option>
                    <option value="SD">South Dakota</option>
                    <option value="TN">Tennessee</option>
                    <option value="TX">Texas</option>
                    <option value="UT">Utah</option>
                    <option value="VT">Vermont</option>
                    <option value="VA">Virginia</option>
                    <option value="WA">Washington</option>
                    <option value="WV">West Virginia</option>
                    <option value="WI">Wisconsin</option>
                    <option value="WY">Wyoming</option>
                  </select>
                  {errorMessages.errorState ? (
                    <span className="field_errors" style={{ color: "#c70000" }}>
                      Please enter state
                    </span>
                  ) : null}
                </div>

                <div className="col-md-4 my-1">
                  <input
                    type="text"
                    onChange={handleChange}
                    className="form-control m-0"
                    id="Zip"
                    name="Zip"
                    value={customerData.Zip}
                    placeholder="Zipcode"
                    required
                  />
                  {errorMessages.errorZip ? (
                    <span className="field_errors" style={{ color: "#c70000" }}>
                      Please write zipcode
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4 p-2">

            <div className="card-body">
              <div className="form-check">
                <div className="I_agree">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="agree"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="agree">
                    I agree to the <Link to="/terms-and-conditions">Terms & Conditions</Link>, <Link to="/refund-policy">Refund Policy</Link> and <Link to="/privacy-policy">Privacy Policy</Link>
                  </label>
                </div>
                {errorMessages.errorAgree ? (
                  <span className="field_errors" style={{ color: "#c70000" }}>
                    Please agree to the Terms & Conditions.
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          {/* ── Checkout button ── */}
          <button
            className="w-100 btn btn-primary submit_btn btn_black"
            type="button"
            id="checkout-btn"
            onClick={Checkout}
            disabled={isLoading}
          >
            {isLoading ? <span className="button-spinner me-2"></span> : null}{" "}
            Checkout
          </button>

          {errorState ? (
            <span
              className="field_errors"
              style={{ color: "red", display: "block", marginTop: 8, fontFamily: 'sans-serif' }}
            >
              {errorState}
            </span>
          ) : null}

          {/* <span
            className="field_errors"
            style={{ color: "red", display: "block", marginTop: 8, fontFamily: 'sans-serif' }}
          >{showingError}</span> */}

        </>
      ) : clientSecret ? (
        // ── Step 2a: Stripe Card Element ready ────────────────────────────────
        <Elements
          stripe={stripePromise}
          options={{ clientSecret, appearance: stripeAppearance }}
        >
          <StripePaymentForm
            total={total}
            quantity={quantity}
            navigate={navigate}
            orderId={orderData?.orderId}
            clientSecret={clientSecret}
            customerData={customerData}
          />
        </Elements>
      ) : paymentIntentError ? (
        // ── Step 2b: PaymentIntent creation failed ────────────────────────────
        <>
          <div className="card mb-4 p-2">
            <div className="card-header py-3">
              <h4 className="mb-0">Payment Details</h4>
            </div>
            <div className="card-body">
              <span className="field_errors" style={{ color: "#c70000" }}>
                {paymentIntentError}
              </span>
            </div>
          </div>
          <button
            className="w-100 btn btn-primary submit_btn btn_black"
            type="button"
            disabled
          >
            Pay Now
          </button>
        </>
      ) : (
        // ── Step 2c: Waiting for PaymentIntent (loading) ──────────────────────
        <>
          <div className="card mb-4 p-2">
            <div className="card-header py-3">
              <h4 className="mb-0">Payment Details</h4>
            </div>
            <div className="card-body">
              <div style={{ color: "#f0f0f0", fontSize: 14, padding: "12px 0" }}>
                <span className="button-spinner me-2"></span> Initializing payment form...
              </div>
            </div>
          </div>
          <button
            className="w-100 btn btn-primary submit_btn btn_black"
            type="button"
            disabled
          >
            Pay Now
          </button>
        </>
      )}
    </>
  );
}

export default CheckoutForm;
